'use client';

import Link from 'next/link';
import Autoplay from 'embla-carousel-autoplay';
import map from 'lodash/map';
import Button from '@/components/shared/Button';
import * as Carousel from '@/components/ui/Carousel';
import { CarrouselItem } from '@/components/ui/HelpContactSection/CarrouselItem';
import type { Media as MediaType, Link as LinkType } from '@/graphql/common/types';
import { ChatWomanIcon, ArrowRightIcon } from '@/icons';
import { onlyNumbers } from '@/utils/onlyNumbers';

type CarrouselProps = {
  slides?: {
    id: string;
    media?: MediaType;
    title?: string;
    description?: string;
    link?: LinkType;
    fileLink?: LinkType;
    phone?: string;
  }[];
};

export const Carrousel = ({ slides }: CarrouselProps) => (
  <Carousel.Root
    opts={{ loop: true }}
    plugins={[Autoplay({ delay: 10000 })]}
    className="overflow-hidden"
  >
    <Carousel.Content>
      {map(slides, (slide) => (
        <Carousel.Item key={slide.id}>
          <CarrouselItem
            img={{
              src: slide.media?.url,
              alt: slide.media?.alternativeText
            }}
            title={slide.title}
            description={slide.description}
            actions={
              <div className="flex flex-col flex-wrap items-center gap-4 lg:flex-row lg:gap-6">
                {slide.link && (
                  <Button asChild>
                    <Link
                      href={slide.link?.url ?? ''}
                      target={slide.link?.target && `_${slide.link.target}`}
                      aria-label={slide.link?.ariaLabel ?? ''}
                      className="w-fit"
                    >
                      {slide.link.label}
                    </Link>
                  </Button>
                )}

                {slide.fileLink && (
                  <Link
                    href={slide.fileLink?.url ?? ''}
                    target={slide.fileLink?.target && `_${slide.fileLink.target}`}
                    aria-label={slide.fileLink?.ariaLabel ?? ''}
                    className="flex items-center gap-2 text-sm font-semibold uppercase leading-4 tracking-[1px] text-blue-500"
                  >
                    {slide.fileLink.label} <ArrowRightIcon />
                  </Link>
                )}
                {slide.phone && (
                  <div className="flex w-[200px] items-center justify-center gap-2">
                    <ChatWomanIcon className="text-blue-500" />
                    <a
                      href={`tel:${onlyNumbers(slide.phone)}`}
                      className="text-2xl font-bold	text-blue-500"
                    >
                      {slide.phone}
                    </a>
                  </div>
                )}
              </div>
            }
          >
            <div className="flex gap-4 pt-10">
              <Carousel.PreviousButton />
              <Carousel.NextButton />
            </div>
          </CarrouselItem>
        </Carousel.Item>
      ))}
    </Carousel.Content>
  </Carousel.Root>
);
